import { FieldPath, FieldValues } from 'react-hook-form/dist/types';
import { Controller, ControllerProps, ControllerRenderProps } from 'react-hook-form';
import { ComponentPropsWithoutRef, FC } from 'react';
import * as _ from 'lodash';

interface FormControlProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TComponentProps extends ComponentPropsWithoutRef<any> = ComponentPropsWithoutRef<any>
> {
  control: ControllerProps<TFieldValues, TName>['control'];
  name: ControllerProps<TFieldValues, TName>['name'];
  rules?: ControllerProps<TFieldValues, TName>['rules'];
  defaultValue?: ControllerProps<TFieldValues, TName>['defaultValue'];
  required?: boolean | string;
  setValueAs?: (
    value: string & ControllerRenderProps<TFieldValues, TName>['value']
  ) => ControllerRenderProps<TFieldValues, TName>['value'];
  ComponentProps: TComponentProps;
  Component: FC<
    TComponentProps &
      ControllerRenderProps<TFieldValues, TName> & {
        required?: boolean;
        error?: boolean;
        defaultValue?: ControllerProps<TFieldValues, TName>['defaultValue'];
      }
  >;
}

export function FormControl<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TComponentProps extends ComponentPropsWithoutRef<any> = ComponentPropsWithoutRef<any>
>({
  control,
  name,
  rules,
  defaultValue,
  required,
  setValueAs,
  Component,
  ComponentProps
}: FormControlProps<TFieldValues, TName, TComponentProps>) {
  return (
    <Controller
      control={control}
      name={name}
      rules={{ ...rules, required }}
      defaultValue={defaultValue}
      render={({ field, fieldState: { error: fieldError } }) => {
        const fieldAugmentation =
          setValueAs === undefined
            ? { ...field, value: field.value ?? ('' as any) }
            : {
                ...field,
                onChange: (event: any) => {
                  field.onChange(
                    event.target && typeof event.target.value === 'string'
                      ? { ...event, target: { ...event.target, value: setValueAs(event.target.value) } }
                      : event
                  );
                }
              };

        const requiredAugmentation = required === undefined ? {} : { required: Boolean(required) };
        const errorAugmentation =
          fieldError === undefined ||
          // If the input is not currently required and its blank, then we want to hide the error
          // This is relevant in cases where the required property of one input depends on the value of another
          (required !== true && _.isEmpty(control?._formValues?.[name]))
            ? {}
            : fieldError.message === undefined
            ? { error: true }
            : { error: true, helperText: fieldError.message };

        const defaultValueAugmentation = defaultValue === undefined ? {} : { defaultValue };

        return (
          <Component
            {...{
              ...ComponentProps,
              ...requiredAugmentation,
              ...errorAugmentation,
              ...defaultValueAugmentation,
              ...fieldAugmentation
            }}
          />
        );
      }}
    />
  );
}
